import {Fade, Slide} from '@aboutyou/transitions';
import React, {useCallback, useRef} from 'react';
import {createPortal} from 'react-dom';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {Z_INDEXES} from '../../../constants/layers';
import {useKeyPress} from '../../../hooks/useKeyPress/useKeyPress';
import {useLockBodyScroll} from '../../../hooks/useLockBodyScroll/useLockBodyScroll';
import {useFlyoutPortalElement} from '../../../hooks/usePortalElement/useFlyoutPortalElement';
import {BlurredOverlay} from '../BlurredOverlay/BlurredOverlay';
import {ContentOpener} from '../ContentPopper/ContentOpener';
import {ContentPopperBaseProps} from '../ContentPopper/ContentPopper';
import {Icon} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';
import {
  ANIMATION_DURATION,
  CLICK_OUTSIDE_LAYOUT_SWIPABLE,
  DraggableBottomsheetProps,
  useDraggableBottomsheet,
} from './useDraggableBottomsheet';

export type FlyoutProps = ContentPopperBaseProps & {
  /**
   * use `renderInPortal` flag to render the modal in a separate portal element
   * This is needed in cases where the flyout has z-index issues OR if an action removes
   * the parent element, which would not animate the flyout on close
   */
  renderInPortal?: boolean;
  onUnmount?: VoidFunction;
  aboveHeaderElement?: React.ReactNode;
  enableFilterFlyout?: boolean;
  // Percentage of the Flyout content to drag down, before triggering the closing of the Flyout
  draggedPercentageToClose?: DraggableBottomsheetProps['draggedPercentageToClose'];
};

export const Flyout = ({
  headerContent,
  openerContent,
  bodyContent,
  onClose,
  onOpenerClick,
  open: isOpen,
  isActive,
  dataTestId = 'bottomSheet',
  showHeaderDivider,
  className,
  renderInPortal,
  onUnmount,
  aboveHeaderElement,
  enableFilterFlyout,
  draggedPercentageToClose = 0.5,
  ...rest
}: FlyoutProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const clickOutsideLayoutRef = useRef<HTMLDivElement | null>(null);

  useKeyPress({key: 'Escape', onKeyDown: onClose, disabled: !isOpen});

  const onCloseFlyout = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClose?.();
    },
    [onClose],
  );

  useLockBodyScroll({lockScroll: isOpen});

  const {passRefThrough} = useDraggableBottomsheet({
    onClose,
    draggedPercentageToClose,
    dialogRef,
    headerRef,
  });

  const flyoutContent = (
    <Fade
      onUnmount={onUnmount}
      mounted={isOpen}
      duration={ANIMATION_DURATION}
      delay={{
        mount: 0,
        unmount: ANIMATION_DURATION / 4,
      }}
    >
      <BlurredOverlay
        aria-labelledby="bottomSheet"
        aria-modal="true"
        data-testid={dataTestId}
        role="dialog"
        tabIndex={-1}
        className={className}
        $zIndex={Z_INDEXES.FLYOUT_FADE}
      >
        <ClickOutsideLayout
          ref={clickOutsideLayoutRef}
          data-testid="clickOutsideLayout"
          data-swipable={CLICK_OUTSIDE_LAYOUT_SWIPABLE}
          onClick={onCloseFlyout}
        />
        <Slide mounted={isOpen} direction="up" duration={ANIMATION_DURATION}>
          <Wrapper $enableFilterFlyout={!!enableFilterFlyout}>
            <FloatingElementWrapper $enableFilterFlyout={!!enableFilterFlyout}>
              {aboveHeaderElement}
              <Dialog
                data-testid="flyoutDialog"
                visible={isOpen}
                $enableFilterFlyout={!!enableFilterFlyout}
                ref={dialogRef}
              >
                <Content
                  {...rest}
                  $enableFilterFlyout={!!enableFilterFlyout}
                  ref={dialogRef}
                >
                  <Header
                    data-testid={
                      dataTestId ? `${dataTestId}Header` : 'bottomSheetHeader'
                    }
                    onClick={onCloseFlyout}
                    role="button"
                    showBorder={Boolean(showHeaderDivider)}
                    tabIndex={0}
                    ref={passRefThrough}
                  >
                    <HeaderToggleIcon
                      asset={GenericIconName.ChevronDown}
                      data-testid="bottomSheetHeaderIcon"
                      height="6"
                      width="22"
                    />
                    {headerContent}
                  </Header>
                  {bodyContent}
                </Content>
              </Dialog>
            </FloatingElementWrapper>
          </Wrapper>
        </Slide>
      </BlurredOverlay>
    </Fade>
  );

  const content = openerContent ? (
    <ContentOpener
      isOpen={isOpen}
      isActive={isActive}
      openerType="single-arrow"
      data-testid={dataTestId ? `${dataTestId}Opener` : 'opener'}
      onClick={onOpenerClick}
    >
      {openerContent}
    </ContentOpener>
  ) : null;

  if (renderInPortal) {
    return (
      <>
        {content}
        {<RenderInPortal>{flyoutContent}</RenderInPortal>}
      </>
    );
  }

  return (
    <>
      {content}
      {flyoutContent}
    </>
  );
};

const RenderInPortal: React.FC<React.PropsWithChildren> = ({children}) => {
  const flyoutPortalElement = useFlyoutPortalElement();

  if (!flyoutPortalElement) {
    return null;
  }

  return createPortal(<>{children}</>, flyoutPortalElement);
};

const ClickOutsideLayout = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
`;

const Wrapper = styled.div<{$enableFilterFlyout: boolean}>`
  position: relative;
  z-index: ${Z_INDEXES.FLYOUT_FADE};
  display: flex;
  align-items: flex-end;
  width: 100%;
  ${props =>
    props.$enableFilterFlyout
      ? css`
          height: 100%;
          margin: 0 auto 0;
        `
      : css`
          height: calc(100% - 4vh);
          margin: 4vh auto 0;
        `}
  pointer-events: none;
  touch-action: none;
`;

const FloatingElementWrapper = styled.div<{$enableFilterFlyout?: boolean}>`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  max-height: 100%;
  ${props =>
    props.$enableFilterFlyout
      ? css`
          height: 100%;
        `
      : ''}
`;

const Dialog = styled.div<{visible?: boolean; $enableFilterFlyout: boolean}>`
  position: relative;
  width: 100%;
  ${props =>
    props.$enableFilterFlyout ? 'height: 100%;' : 'max-height: 100%;'}
  background: ${COLORS.White};
  border-radius: 8px 8px 0 0;
  pointer-events: ${({visible}) => (visible ? 'all' : 'none')};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Content = styled.div<{$enableFilterFlyout: boolean}>`
  ${props =>
    props.$enableFilterFlyout ? 'height: 100%;' : 'max-height: 100%;'}
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.header<{showBorder: boolean}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 0 0.75rem;
  border-bottom: ${({showBorder}) =>
    showBorder ? `1px solid ${COLORS.Grey3}` : 'none'};
  cursor: pointer;
  flex-shrink: 0;
  overscroll-behavior: contain;

  &:focus {
    outline: none;
  }
`;

const HeaderToggleIcon = styled(Icon)`
  fill: ${COLORS.Black};
  margin-bottom: 0.375rem;
`;
